/**
 * Command Centre · Trust & Safety
 *
 * Founder-facing dashboard showing:
 *   1. Live NeMo Guardrails runtime status (loaded, rails, model)
 *   2. Live block ledger from /api/admin/safety/blocks/recent
 *   3. Defence-in-depth layer map (from the public stats endpoint)
 *   4. AI Social Post Generator — one-click weekly post for Twitter/LinkedIn
 *      using Claude (caption) + Gemini Nano Banana (image, with PIL fallback)
 *
 * Drop-in companion to the existing /admin/safety page which handles the
 * legacy guardrails/keyword/handovers UI. This page focuses on the new
 * NeMo + safety_filter + transparency stack shipped in Sprint 184.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import AdminLayout from '../../components/layout/AdminLayout';
import { apiClient } from '../../api/client';
import {
  ShieldCheck, ShieldAlert, Activity, Sparkles, Copy, Download, RefreshCw,
  CheckCircle2, XCircle, Loader2, Twitter, Linkedin, AlertTriangle, Zap, X,
} from 'lucide-react';
import { toast } from 'sonner';

const COLORS = {
  card: '#FFFFFF',
  border: '#E0E0E0',
  text: '#1F1F2C',
  subtle: '#6B6B7B',
  orange: '#F5831F',
  blue: '#1B4FA0',
  emerald: '#10B981',
  cream: '#FAF6EE',
};

const AdminTrustSafety = () => {
  const [nemo, setNemo] = useState(null);
  const [publicStats, setPublicStats] = useState(null);
  const [recentBlocks, setRecentBlocks] = useState([]);
  const [mediaHealth, setMediaHealth] = useState(null);
  const [mediaHealthRecent, setMediaHealthRecent] = useState([]);
  const [loading, setLoading] = useState(true);

  // Social post panel
  const [platform, setPlatform] = useState('twitter');
  const [useAiImage, setUseAiImage] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [latestPost, setLatestPost] = useState(null);
  const [history, setHistory] = useState([]);

  // Drill-down state: which KPI tile is active. null = no filter (show all).
  // Possible values: 'nemo' | '30d' | 'total' | 'screened'
  const [activeFilter, setActiveFilter] = useState(null);
  const ledgerRef = useRef(null);
  const nemoCardRef = useRef(null);

  const scrollIntoView = (ref) => {
    if (ref?.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleTileClick = (filter) => {
    if (filter === 'runtime') {
      scrollIntoView(nemoCardRef);
      return;
    }
    setActiveFilter((prev) => (prev === filter ? null : filter));
    scrollIntoView(ledgerRef);
  };

  const filteredBlocks = (() => {
    if (!activeFilter) return recentBlocks;
    if (activeFilter === 'nemo') {
      return recentBlocks.filter((b) => {
        const ctx = (b.context || '').toLowerCase();
        const rsn = (b.reason || '').toLowerCase();
        return ctx.includes('nemo') || rsn.includes('nemo') || rsn.startsWith('nemo_');
      });
    }
    if (activeFilter === '30d') {
      const cutoff = Date.now() - 30 * 24 * 60 * 60 * 1000;
      return recentBlocks.filter((b) => {
        if (!b.created_at) return false;
        const t = new Date(b.created_at).getTime();
        return Number.isFinite(t) && t >= cutoff;
      });
    }
    return recentBlocks;
  })();

  const filterLabel = {
    nemo: 'NeMo blocks only',
    '30d': 'Last 30 days',
    screened: 'All blocks (full history)',
  }[activeFilter];

  const load = useCallback(async () => {
    try {
      const [n, p, b, h, mh, mhr] = await Promise.all([
        apiClient.request('/api/admin/safety/nemo/status'),
        apiClient.request('/api/public/safety/stats'),
        apiClient.request('/api/admin/safety/blocks/recent?limit=15'),
        apiClient.request('/api/admin/safety/social-post/recent?limit=5'),
        apiClient.request('/api/admin/media-health/summary').catch(() => null),
        apiClient.request('/api/admin/media-health/recent?limit=10').catch(() => ({ events: [] })),
      ]);
      setNemo(n);
      setPublicStats(p);
      setRecentBlocks(b.rows || []);
      setHistory(h.rows || []);
      if ((h.rows || []).length > 0) setLatestPost(h.rows[0]);
      setMediaHealth(mh);
      setMediaHealthRecent(mhr.events || []);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load safety data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleGenerate() {
    setGenerating(true);
    try {
      const res = await apiClient.request('/api/admin/safety/social-post/generate', {
        method: 'POST',
        body: JSON.stringify({ platform, use_ai_image: useAiImage }),
      });
      setLatestPost(res);
      setHistory((prev) => [res, ...prev].slice(0, 5));
      toast.success(`Social post ready — ${res.image_source === 'nano_banana' ? 'AI image' : 'composite image'} generated`);
    } catch (e) {
      console.error(e);
      toast.error('Generation failed — check backend logs');
    } finally {
      setGenerating(false);
    }
  }

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text).then(() => toast.success(`${label} copied`));
  };

  if (loading) {
    return (
      <AdminLayout title="Trust & Safety" subtitle="NeMo Guardrails · live blocks · AI social posts">
        <div className="flex items-center justify-center py-24">
          <Loader2 className="animate-spin" size={32} style={{ color: COLORS.orange }} />
        </div>
      </AdminLayout>
    );
  }

  const stats = publicStats?.headline || {};
  const nemoLive = nemo?.runtime?.loaded;

  return (
    <AdminLayout title="Trust & Safety" subtitle="NeMo Guardrails · live block ledger · AI social posts" data-testid="admin-trust-safety">
      {/* HEADLINE STRIP */}
      <div className="flex items-center justify-end gap-2 mb-3 text-xs" data-testid="ts-channels-bar">
        <span style={{ color: COLORS.subtle }}>Official channels:</span>
        <a href="https://x.com/GoHiBFF" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 px-3 py-1 rounded-full hover:underline" style={{ background: '#FFFFFF', border: `1px solid ${COLORS.border}`, color: COLORS.text }} data-testid="channel-x-link">
          <Twitter size={11} /> @GoHiBFF
        </a>
        <a href="https://www.linkedin.com/in/hibff/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 px-3 py-1 rounded-full hover:underline" style={{ background: '#FFFFFF', border: `1px solid ${COLORS.border}`, color: COLORS.text }} data-testid="channel-linkedin-link">
          <Linkedin size={11} /> linkedin.com/in/hibff
        </a>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="ts-headline-strip">
        <RuntimeTile live={nemoLive} onClick={() => handleTileClick('runtime')} />
        <KpiTile label="Messages screened" value={stats.messages_screened} color={COLORS.text} testid="kpi-screened" onClick={() => handleTileClick('screened')} active={activeFilter === 'screened'} />
        <KpiTile
          label="Blocks (last 30d / total)"
          value={`${(stats.blocks_last_30d ?? 0).toLocaleString()} / ${(stats.total_blocks ?? 0).toLocaleString()}`}
          color={COLORS.orange}
          testid="kpi-blocks"
          onClick={() => handleTileClick('30d')}
          active={activeFilter === '30d'}
        />
        <KpiTile label="NeMo blocks" value={stats.nemo_blocks_total} color={COLORS.blue} testid="kpi-nemo-blocks" onClick={() => handleTileClick('nemo')} active={activeFilter === 'nemo'} />
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        {/* LEFT — block ledger */}
        <section ref={ledgerRef} className="lg:col-span-2 bg-white p-5 rounded-2xl" style={{ border: `1px solid ${COLORS.border}` }} data-testid="ts-blocks-card">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-lg font-bold" style={{ color: COLORS.text }}>Live block ledger</h2>
              {activeFilter && (
                <button
                  type="button"
                  onClick={() => setActiveFilter(null)}
                  className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold"
                  style={{ background: '#FFE7D2', color: COLORS.orange, border: `1px solid ${COLORS.orange}` }}
                  data-testid="ts-active-filter-pill"
                  title="Click to clear filter"
                >
                  {filterLabel}
                  <X size={12} />
                </button>
              )}
              <span className="text-xs" style={{ color: COLORS.subtle }} data-testid="ts-ledger-count">
                {filteredBlocks.length} of {recentBlocks.length}
              </span>
            </div>
            <button onClick={load} className="text-xs flex items-center gap-1.5 px-3 py-1.5 rounded-full" style={{ color: COLORS.subtle, border: `1px solid ${COLORS.border}` }} data-testid="ts-refresh-btn">
              <RefreshCw size={12} /> Refresh
            </button>
          </div>
          {filteredBlocks.length === 0 ? (
            <p className="text-sm py-6 text-center" style={{ color: COLORS.subtle }} data-testid="ts-blocks-empty">
              {activeFilter ? 'No blocks match this filter in the current window.' : 'No blocks recorded yet.'}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ color: COLORS.subtle, borderBottom: `1px solid ${COLORS.border}` }}>
                    <th className="text-left p-2 font-semibold text-xs uppercase tracking-wider">When</th>
                    <th className="text-left p-2 font-semibold text-xs uppercase tracking-wider">Context</th>
                    <th className="text-left p-2 font-semibold text-xs uppercase tracking-wider">Reason</th>
                    <th className="text-left p-2 font-semibold text-xs uppercase tracking-wider">Sample</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBlocks.map((b, i) => (
                    <tr key={i} style={{ borderBottom: `1px solid ${COLORS.border}` }} data-testid={`block-row-${i}`}>
                      <td className="p-2 text-xs whitespace-nowrap" style={{ color: COLORS.subtle }}>
                        {b.created_at ? new Date(b.created_at).toLocaleString() : '—'}
                      </td>
                      <td className="p-2 text-xs">
                        <span className="px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider" style={{ background: '#F4F4F8', color: COLORS.subtle }}>
                          {b.context || '—'}
                        </span>
                      </td>
                      <td className="p-2 text-xs font-mono" style={{ color: COLORS.orange }}>{b.reason || '—'}</td>
                      <td className="p-2 text-xs max-w-[300px] truncate" style={{ color: COLORS.text }} title={b.sample}>
                        {b.sample || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* RIGHT — NeMo status + retention */}
        <aside className="space-y-4">
          <div ref={nemoCardRef} className="bg-white p-5 rounded-2xl" style={{ border: `1px solid ${COLORS.border}` }} data-testid="ts-nemo-card">
            <div className="flex items-center gap-2 mb-4">
              <ShieldCheck size={20} style={{ color: nemoLive ? COLORS.emerald : COLORS.subtle }} />
              <h3 className="font-bold" style={{ color: COLORS.text }}>NeMo Guardrails runtime</h3>
            </div>
            <Row label="Status" value={nemoLive ? 'LIVE' : 'OFFLINE'} valueColor={nemoLive ? COLORS.emerald : '#DC2626'} />
            <Row label="Loaded at" value={nemo?.runtime?.loaded_at?.split('T')[1]?.split('.')[0] || '—'} />
            <Row label="Model" value="gpt-4o-mini" />
            <Row label="Input rails" value={(nemo?.runtime?.rails_enabled?.input || []).join(', ')} />
            <Row label="Output rails" value={(nemo?.runtime?.rails_enabled?.output || []).join(', ')} />
            <Row label="NeMo blocks logged" value={nemo?.metrics?.nemo_blocks_total ?? 0} />
          </div>

          <div className="bg-white p-5 rounded-2xl" style={{ border: `1px solid ${COLORS.border}` }} data-testid="ts-retention-card">
            <h3 className="font-bold mb-3" style={{ color: COLORS.text }}>Retention & frameworks</h3>
            <Row label="Audit log" value={`${publicStats?.retention?.audit_log_days || '—'} days`} />
            <Row label="Block evidence" value={`${publicStats?.retention?.block_evidence_days || '—'} days`} />
            <Row label="Teen messages" value={`${publicStats?.retention?.teen_messages_days || '—'} days`} />
            <div className="mt-3 pt-3 border-t flex flex-wrap gap-1" style={{ borderColor: COLORS.border }}>
              {(publicStats?.frameworks || []).map((f) => (
                <span key={f} className="text-[10px] px-2 py-1 rounded-full" style={{ background: '#F4F4F8', color: COLORS.subtle }}>{f}</span>
              ))}
            </div>
          </div>

          {/* Sprint 206 — Media Health (voice + AvA video failures) */}
          <div className="bg-white p-5 rounded-2xl" style={{ border: `1px solid ${COLORS.border}` }} data-testid="ts-media-health-card">
            <div className="flex items-center gap-2 mb-3">
              <Activity size={18} style={{ color: mediaHealth?.is_healthy ? COLORS.emerald : COLORS.orange }} />
              <h3 className="font-bold" style={{ color: COLORS.text }}>AvA media health</h3>
              {mediaHealth && (
                <span
                  className="ml-auto px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-semibold"
                  style={{
                    background: mediaHealth.is_healthy ? '#E8F8EE' : '#FFF1F2',
                    color: mediaHealth.is_healthy ? COLORS.emerald : '#DC2626',
                  }}
                  data-testid="media-health-status"
                >
                  {mediaHealth.is_healthy ? 'Healthy' : `${mediaHealth.windows?.last_24h?.total || 0} fails / 24h`}
                </span>
              )}
            </div>
            {mediaHealth ? (
              <>
                <Row label="Last 24h" value={mediaHealth.windows?.last_24h?.total ?? 0} />
                <Row label="Last 7d" value={mediaHealth.windows?.last_7d?.total ?? 0} />
                <Row label="Last 30d" value={mediaHealth.windows?.last_30d?.total ?? 0} />
                {mediaHealth.most_recent && (
                  <div className="mt-3 pt-3 border-t" style={{ borderColor: COLORS.border }}>
                    <p className="text-[10px] uppercase tracking-wider" style={{ color: COLORS.subtle }}>Most recent</p>
                    <p className="text-xs font-mono mt-1" style={{ color: COLORS.orange }}>{mediaHealth.most_recent.event_type}</p>
                    <p className="text-[11px]" style={{ color: COLORS.subtle }}>
                      {new Date(mediaHealth.most_recent.created_at).toLocaleString()}
                    </p>
                  </div>
                )}
                {mediaHealthRecent.length > 0 && (
                  <details className="mt-3 pt-3 border-t" style={{ borderColor: COLORS.border }}>
                    <summary className="text-xs cursor-pointer" style={{ color: COLORS.subtle }} data-testid="media-health-recent-toggle">
                      Recent failures ({mediaHealthRecent.length})
                    </summary>
                    <ul className="mt-2 space-y-1.5">
                      {mediaHealthRecent.slice(0, 10).map((e) => (
                        <li key={e.event_id} className="text-[11px]" style={{ color: COLORS.text }}>
                          <span className="font-mono" style={{ color: COLORS.orange }}>{e.event_type}</span>
                          <span style={{ color: COLORS.subtle }}> · {new Date(e.created_at).toLocaleTimeString()}</span>
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </>
            ) : (
              <p className="text-xs" style={{ color: COLORS.subtle }}>Telemetry unavailable.</p>
            )}
          </div>
        </aside>
      </div>

      {/* DEFENCE-IN-DEPTH LAYERS */}
      <section className="bg-white p-5 rounded-2xl mt-5" style={{ border: `1px solid ${COLORS.border}` }} data-testid="ts-layers-card">
        <div className="flex items-center gap-2 mb-4">
          <Activity size={20} style={{ color: COLORS.orange }} />
          <h2 className="text-lg font-bold" style={{ color: COLORS.text }}>Defence-in-depth — every message passes 7 gates</h2>
        </div>
        <div className="grid md:grid-cols-2 gap-2">
          {(publicStats?.layers || []).map((l) => (
            <div key={l.n} className="flex items-start gap-3 p-3 rounded-xl" style={{ background: '#FAFAFA', border: `1px solid ${COLORS.border}` }} data-testid={`ts-layer-${l.n}`}>
              <div className="w-7 h-7 rounded-full shrink-0 flex items-center justify-center font-bold text-xs" style={{ background: '#FFE7D2', color: COLORS.orange }}>
                {l.n}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold" style={{ color: COLORS.text }}>{l.name}</p>
                <p className="text-[11px] font-mono mt-0.5" style={{ color: COLORS.subtle }}>{l.engine}</p>
              </div>
              <div className="text-right shrink-0">
                <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full`} style={{
                  background: l.scope === 'input' ? '#E8F8EE' : l.scope === 'output' ? '#E8F0FE' : '#F4F4F8',
                  color: l.scope === 'input' ? COLORS.emerald : l.scope === 'output' ? COLORS.blue : COLORS.subtle,
                }}>{l.scope}</span>
                {l.latency_ms != null && (
                  <p className="text-[10px] font-mono mt-1" style={{ color: COLORS.subtle }}>~{l.latency_ms}ms</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* AI SOCIAL POST GENERATOR */}
      <section className="bg-gradient-to-br from-[#FFFBF5] to-[#FAF6EE] p-6 rounded-2xl mt-5" style={{ border: `1px solid ${COLORS.border}` }} data-testid="ts-social-card">
        <div className="flex items-start justify-between flex-wrap gap-4 mb-5">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Sparkles size={20} style={{ color: COLORS.orange }} />
              <h2 className="text-lg font-bold" style={{ color: COLORS.text }}>AI weekly social post</h2>
            </div>
            <p className="text-sm" style={{ color: COLORS.subtle }}>
              Claude writes the caption, Gemini Nano Banana renders the card (PIL composite if AI image unavailable).
              One-click. Live numbers baked in.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="inline-flex rounded-full" style={{ background: '#FFFFFF', border: `1px solid ${COLORS.border}` }}>
              <button
                onClick={() => setPlatform('twitter')}
                data-testid="platform-twitter-btn"
                className={`px-4 py-2 text-sm rounded-full flex items-center gap-1.5 ${platform === 'twitter' ? 'text-white' : ''}`}
                style={{ background: platform === 'twitter' ? COLORS.text : 'transparent', color: platform === 'twitter' ? '#FFF' : COLORS.text }}
              >
                <Twitter size={14} /> Twitter
              </button>
              <button
                onClick={() => setPlatform('linkedin')}
                data-testid="platform-linkedin-btn"
                className={`px-4 py-2 text-sm rounded-full flex items-center gap-1.5 ${platform === 'linkedin' ? 'text-white' : ''}`}
                style={{ background: platform === 'linkedin' ? COLORS.text : 'transparent', color: platform === 'linkedin' ? '#FFF' : COLORS.text }}
              >
                <Linkedin size={14} /> LinkedIn
              </button>
            </div>
            <label className="text-xs flex items-center gap-1.5 px-3 py-2 rounded-full cursor-pointer" style={{ background: '#FFFFFF', border: `1px solid ${COLORS.border}`, color: COLORS.subtle }}>
              <input type="checkbox" checked={useAiImage} onChange={(e) => setUseAiImage(e.target.checked)} data-testid="use-ai-image-toggle" />
              AI image
            </label>
            <button
              onClick={handleGenerate}
              disabled={generating}
              data-testid="generate-social-post-btn"
              className="px-5 py-2.5 rounded-full flex items-center gap-2 font-semibold text-white"
              style={{ background: COLORS.orange, opacity: generating ? 0.6 : 1 }}
            >
              {generating ? <Loader2 className="animate-spin" size={16} /> : <Zap size={16} />}
              {generating ? 'Generating…' : 'Generate post'}
            </button>
          </div>
        </div>

        {latestPost && (
          <div className="grid md:grid-cols-5 gap-5" data-testid="ts-social-preview">
            <div className="md:col-span-2">
              {latestPost.image_url ? (
                <img
                  src={latestPost.image_url}
                  alt={latestPost.alt}
                  className="w-full rounded-2xl shadow-lg"
                  style={{ border: `1px solid ${COLORS.border}` }}
                  data-testid="social-post-image"
                />
              ) : (
                <div className="aspect-square rounded-2xl flex items-center justify-center" style={{ background: COLORS.cream }}>
                  <p className="text-xs" style={{ color: COLORS.subtle }}>Generating…</p>
                </div>
              )}
              <div className="flex items-center justify-between mt-2 text-[11px]" style={{ color: COLORS.subtle }}>
                <span>Source: <strong style={{ color: latestPost.image_source === 'nano_banana' ? COLORS.emerald : COLORS.subtle }}>{latestPost.image_source}</strong></span>
                <a href={latestPost.image_url} download={`hibff-social-${platform}.png`} className="flex items-center gap-1 hover:underline" data-testid="download-image-btn">
                  <Download size={11} /> Download
                </a>
              </div>

              {/* One-click compose buttons — drop the caption straight into X / LinkedIn */}
              <div className="flex flex-col gap-2 mt-3">
                <a
                  href={`https://x.com/intent/post?text=${encodeURIComponent(latestPost.body || '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 py-2.5 rounded-full font-semibold text-white"
                  style={{ background: COLORS.text }}
                  data-testid="post-to-x-btn"
                >
                  <Twitter size={14} /> Post to X · @GoHiBFF
                </a>
                <a
                  href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent('https://hibff.com/trust-safety')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 py-2.5 rounded-full font-semibold text-white"
                  style={{ background: '#0A66C2' }}
                  data-testid="post-to-linkedin-btn"
                >
                  <Linkedin size={14} /> Share on LinkedIn
                </a>
                <p className="text-[10px] text-center" style={{ color: COLORS.subtle }}>
                  LinkedIn doesn&apos;t accept caption pre-fill — paste it from the field on the right after the dialog opens.
                </p>
              </div>
            </div>
            <div className="md:col-span-3 space-y-3">
              <Field label="Headline" value={latestPost.headline} onCopy={() => copyToClipboard(latestPost.headline, 'Headline')} testid="field-headline" />
              <Field label="Caption" value={latestPost.body} multiline onCopy={() => copyToClipboard(latestPost.body, 'Caption')} testid="field-body" />
              <Field label="Alt text (accessibility)" value={latestPost.alt} onCopy={() => copyToClipboard(latestPost.alt, 'Alt text')} testid="field-alt" />
              <div className="grid grid-cols-3 gap-2 text-xs">
                <Stat label="Screened" value={latestPost.stats?.messages_screened || stats.messages_screened} />
                <Stat label="Blocked 7d" value={latestPost.stats?.blocks_last_7d || 0} />
                <Stat label="NeMo blocks" value={latestPost.stats?.nemo_blocks_total || stats.nemo_blocks_total} />
              </div>
            </div>
          </div>
        )}

        {!latestPost && (
          <p className="text-sm py-8 text-center" style={{ color: COLORS.subtle }}>
            Click &ldquo;Generate post&rdquo; to create the first weekly card.
          </p>
        )}

        {/* History */}
        {history.length > 0 && (
          <div className="mt-6 pt-6 border-t" style={{ borderColor: COLORS.border }} data-testid="ts-social-history">
            <p className="text-xs uppercase tracking-wider mb-3" style={{ color: COLORS.subtle }}>Previously generated</p>
            <div className="flex gap-3 overflow-x-auto pb-2">
              {history.map((p) => (
                <button
                  key={p.token || p.report_id}
                  onClick={() => setLatestPost(p)}
                  className="shrink-0 w-32 text-left rounded-xl overflow-hidden hover:ring-2 hover:ring-orange-300 transition"
                  style={{ border: `1px solid ${COLORS.border}` }}
                  data-testid={`history-card-${p.token || p.report_id}`}
                >
                  <img src={p.image_url} alt="" className="w-full h-32 object-cover" />
                  <div className="p-2 text-[10px]" style={{ color: COLORS.subtle }}>
                    {p.platform} · {p.created_at?.split('T')[0]}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </section>
    </AdminLayout>
  );
};

// ── helpers ──────────────────────────────────────────────────────────────

const RuntimeTile = ({ live, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="p-4 rounded-2xl flex flex-col gap-2 text-left transition hover:shadow-md hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-offset-1"
    style={{
      background: live ? '#E8F8EE' : '#FFF1F2',
      border: `1px solid ${live ? '#A7E2BB' : '#FFD3D8'}`,
      cursor: 'pointer',
    }}
    data-testid="runtime-tile"
    title="Jump to NeMo runtime status"
  >
    <p className="text-[10px] uppercase tracking-wider" style={{ color: COLORS.subtle }}>NeMo Guardrails</p>
    <div className="flex items-center gap-2">
      {live ? <CheckCircle2 size={20} style={{ color: COLORS.emerald }} /> : <XCircle size={20} style={{ color: '#DC2626' }} />}
      <span className="font-bold text-lg" style={{ color: live ? COLORS.emerald : '#DC2626' }}>{live ? 'LIVE' : 'OFFLINE'}</span>
    </div>
  </button>
);

const KpiTile = ({ label, value, color, testid, onClick, active }) => (
  <button
    type="button"
    onClick={onClick}
    className="p-4 rounded-2xl text-left transition hover:shadow-md hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-offset-1"
    style={{
      background: active ? '#FFF7EE' : '#FFFFFF',
      border: `1px solid ${active ? COLORS.orange : COLORS.border}`,
      cursor: onClick ? 'pointer' : 'default',
      boxShadow: active ? '0 0 0 2px rgba(245,131,31,0.18)' : 'none',
    }}
    data-testid={testid}
    title={onClick ? `Filter live block ledger · ${label}` : label}
  >
    <p className="text-[10px] uppercase tracking-wider" style={{ color: COLORS.subtle }}>{label}</p>
    <p className="text-3xl font-black tabular-nums mt-2" style={{ color }}>
      {typeof value === 'number' ? value.toLocaleString() : (value ?? '0')}
    </p>
  </button>
);

const Row = ({ label, value, valueColor }) => (
  <div className="flex items-center justify-between py-1.5 text-sm">
    <span style={{ color: COLORS.subtle }}>{label}</span>
    <span className="font-semibold text-right max-w-[60%] truncate" style={{ color: valueColor || COLORS.text }} title={String(value)}>{value || '—'}</span>
  </div>
);

const Field = ({ label, value, multiline, onCopy, testid }) => (
  <div data-testid={testid}>
    <div className="flex items-center justify-between mb-1">
      <span className="text-[10px] uppercase tracking-wider" style={{ color: COLORS.subtle }}>{label}</span>
      <button onClick={onCopy} className="text-[10px] flex items-center gap-1 hover:underline" style={{ color: COLORS.orange }}>
        <Copy size={10} /> Copy
      </button>
    </div>
    <div
      className={`px-3 py-2 rounded-lg text-sm ${multiline ? 'whitespace-pre-wrap' : 'truncate'}`}
      style={{ background: '#FFFFFF', border: `1px solid ${COLORS.border}`, color: COLORS.text }}
    >
      {value || '—'}
    </div>
  </div>
);

const Stat = ({ label, value }) => (
  <div className="p-2 rounded-lg text-center" style={{ background: '#FFFFFF', border: `1px solid ${COLORS.border}` }}>
    <p className="text-[9px] uppercase tracking-wider" style={{ color: COLORS.subtle }}>{label}</p>
    <p className="font-bold tabular-nums" style={{ color: COLORS.text }}>{(value || 0).toLocaleString()}</p>
  </div>
);

export default AdminTrustSafety;
